import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { TeamMembersListControlsState, useQueryListProjectTeamMembers } from '../../team.queries';
import { useMemo, useState } from 'react';
import { KnownProjectTeamMemberDtoStatus, KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';

const initialControlsState: TeamMembersListControlsState = {
  sortBy: 'firstName',
  sortOrder: KnownSortOrder.Asc,
  status: '',
  searchTerm: ''
};

const searchParamOptions: UseSearchParamsOptions<TeamMembersListControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

const teamMembershipStatusOptions: { label: string; value: KnownProjectTeamMemberDtoStatus | '' }[] = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: KnownProjectTeamMemberDtoStatus.Active },
  { label: 'Removed', value: KnownProjectTeamMemberDtoStatus.Removed },
  { label: 'Invited', value: KnownProjectTeamMemberDtoStatus.Invited },
  { label: 'Suspended', value: KnownProjectTeamMemberDtoStatus.Suspended }
];

export enum TeamMembersListActions {
  INVITE_TEAM_MEMBER = 'INVITE_TEAM_MEMBER',
  INVITE_TEAM_MEMBER_SUCCESS = 'INVITE_TEAM_MEMBER_SUCCESS'
}

export function useTeamMembersList(projectID: string) {
  const [currentAction, setCurrentAction] = useState<TeamMembersListActions | null>(null);
  function setAction(action: TeamMembersListActions | null) {
    setCurrentAction(action);
  }
  const project = useQueryGetProject(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);

  const isExperimentalUser = useHasUIFeatures(['experimental']);

  const team = useQueryListProjectTeamMembers(
    projectID,
    {
      ...queryData,
      includeRemoved: isExperimentalUser.hasAllFeatures
    },
    !isExperimentalUser.isLoading
  );

  const statusOptions = useMemo(() => {
    const externalOnlyOptions = teamMembershipStatusOptions.filter(
      (option) => option.value !== KnownProjectTeamMemberDtoStatus.Removed
    );
    if (isExperimentalUser.isLoading || !isExperimentalUser.hasAllFeatures) {
      return externalOnlyOptions;
    }
    return teamMembershipStatusOptions;
  }, [isExperimentalUser.isLoading, isExperimentalUser.hasAllFeatures]);

  return { project, team, formData, setFormData, actions: { currentAction, setAction }, statusOptions } as const;
}
